/**
 * Canonical map of in-app help-centre links.
 *
 * Centralised so any rename / restructure of `docs.bowerlabs.ai`
 * routes is one PR.  Every "Learn more" link in the product should
 * pull from here — never hard-code a docs URL inline.
 */

const HELP_CENTRE_BASE = 'https://docs.bowerlabs.ai';

export const HELP_LINKS = {
  /** Sharing notes / collections with specific people + Share-to-web. */
  SHARING: `${HELP_CENTRE_BASE}/organisation/sharing-artifacts`,
  /** Workspace ownership transfer + workspace-account management. */
  MANAGE_ACCOUNT: `${HELP_CENTRE_BASE}/workspaces-and-account/manage-account`,
  /** Roles + invitation flow. */
  INVITE_TEAM: `${HELP_CENTRE_BASE}/workspaces-and-account/invite-team-members`,
  /** Cookies + privacy preferences. */
  COOKIES: `${HELP_CENTRE_BASE}/trust-center/cookies`,
  /** HIPAA / PHI compliance docs. */
  HIPAA: `${HELP_CENTRE_BASE}/trust-center/hipaa-compliance`,
  /** Personal preferences. */
  PERSONAL_PREFERENCES: `${HELP_CENTRE_BASE}/ai-and-search/personal-preferences`,
  /** Connectors / managed-MCP-gateway connections. */
  CONNECTORS: `${HELP_CENTRE_BASE}/integrations/connectors`,
  /** Every kind of workspace item (collections, notes, files, protocols, equipment, links) and what an import becomes. */
  ARTIFACTS: `${HELP_CENTRE_BASE}/organisation/workspace-items`,
  /** Collections section of the workspace items overview. Linked from the Collections page. */
  COLLECTIONS: `${HELP_CENTRE_BASE}/organisation/workspace-items#collections`,
  /** The five suggested collections a workspace starts with. Linked from their empty splash pages. */
  SUGGESTED_COLLECTIONS: `${HELP_CENTRE_BASE}/organisation/workspace-items#suggested-collections`,
  /** Equipment: adding instruments and asking Bower about them. Linked from the Equipment page. */
  EQUIPMENT: `${HELP_CENTRE_BASE}/organisation/equipment`,
  /** Protocol Library — building + executing step-by-step procedures. */
  PROTOCOLS: `${HELP_CENTRE_BASE}/organisation/protocols`,
  /** Protocol runs: each protocol's runs and their records, inside Protocols. */
  PROTOCOL_RUNS: `${HELP_CENTRE_BASE}/organisation/protocols#protocol-runs`,
  /** Step cards (a protocol step's video) during a run, in Live. */
  STEP_VIDEOS: `${HELP_CENTRE_BASE}/ai-and-search/live-agent-mode#step-cards`,
  /** Workspace visualizer: the map of how items connect. Linked from its empty state. */
  VISUALIZER: `${HELP_CENTRE_BASE}/organisation/workspace-visualizer`,
  /** Version history of notes and spreadsheets. */
  VERSION_HISTORY: `${HELP_CENTRE_BASE}/capture/version-history`,
  /** Images in the editor, and drawing on them. */
  EDITOR_IMAGES: `${HELP_CENTRE_BASE}/capture/editor#images`,
  /** Workspace instructions Bower follows in every chat. */
  WORKSPACE_INSTRUCTIONS: `${HELP_CENTRE_BASE}/ai-and-search/workspace-instructions`,
  /** What's new. */
  CHANGELOG: `${HELP_CENTRE_BASE}/changelog`,
  /** AI app access — connecting external AI clients (Claude.ai, ChatGPT, Cursor, Zed) via MCP. */
  AI_APP_ACCESS: `${HELP_CENTRE_BASE}/integrations/ai-app-access`,
  /** Keyboard shortcuts reference: navigation, editing, saving, tables. */
  KEYBOARD_SHORTCUTS: `${HELP_CENTRE_BASE}/capture/keyboard-shortcuts`,
  /** Importing files: what each format becomes, limits. Linked from ImportFilesModal. */
  BATCH_IMPORT: `${HELP_CENTRE_BASE}/capture/batch-import`,
  /** What Trash keeps, for how long, and restoring from it. Linked from the Trash page. */
  TRASH: `${HELP_CENTRE_BASE}/organisation/organising-workspace#trash`,
  /** Top-level help. */
  HOME: `${HELP_CENTRE_BASE}/`,
} as const;

export type HelpLinkKey = keyof typeof HELP_LINKS;
